/**
 * CSRF Token Management Service
 *
 * Implements CSRF protection for the driver app by:
 * 1. Fetching CSRF token from backend on app initialization
 * 2. Storing token in memory (not localStorage for security)
 * 3. Providing token to API client for inclusion in request headers
 * 4. Auto-refreshing token when expired (403 CSRF errors)
 *
 * Pattern: Double-submit cookie
 * - Backend sets CSRF token in httpOnly cookie (automatic)
 * - Frontend includes same token in X-CSRF-Token header
 * - Backend validates both match
 *
 * @see packages/workers/src/middleware/csrfProtection.ts
 */

import { logger } from '../utils/logger';

// API base URL - uses production domain by default, overrideable via VITE_API_URL env var
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.gamaleigan.is';

/**
 * In-memory CSRF token storage
 * Not using localStorage to prevent XSS token theft
 */
let csrfToken: string | null = null;

/**
 * Flag to prevent concurrent token fetch requests
 */
let isFetchingToken = false;

/**
 * Promise for concurrent requests to wait on
 */
let tokenFetchPromise: Promise<string | null> | null = null;

/**
 * Fetch CSRF token from backend
 *
 * @returns CSRF token string or null on failure
 */
export async function fetchCsrfToken(): Promise<string | null> {
  // If already fetching, wait for existing request
  if (isFetchingToken && tokenFetchPromise) {
    return tokenFetchPromise;
  }

  // Set fetching flag
  isFetchingToken = true;

  // Create promise for concurrent requests
  tokenFetchPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/csrf-token`, {
        method: 'GET',
        credentials: 'include', // Important: Send cookies (csrf_token cookie)
      });

      if (!response.ok) {
        logger.error('[CSRF] Failed to fetch token:', response.status);
        return null;
      }

      const data = await response.json();
      csrfToken = data.csrf_token || null;

      if (csrfToken) {
        logger.log('[CSRF] Token fetched successfully');
      } else {
        logger.warn('[CSRF] No token in response');
      }

      return csrfToken;
    } catch (error) {
      logger.error('[CSRF] Error fetching token:', error);
      return null;
    } finally {
      isFetchingToken = false;
      tokenFetchPromise = null;
    }
  })();

  return tokenFetchPromise;
}

/**
 * Get current CSRF token
 *
 * @returns Current CSRF token or null if not initialized
 */
export function getCsrfToken(): string | null {
  return csrfToken;
}

/**
 * Refresh CSRF token (called on 403 CSRF errors)
 *
 * @returns Promise that resolves to new token or null
 */
export async function refreshCsrfToken(): Promise<string | null> {
  logger.log('[CSRF] Refreshing token...');
  csrfToken = null; // Clear old token
  return fetchCsrfToken();
}

/**
 * Initialize CSRF token on app start
 * Call this in main.tsx before rendering app
 *
 * @returns Promise that resolves when token is fetched
 */
export async function initializeCsrf(): Promise<void> {
  await fetchCsrfToken();
}

/**
 * Check if CSRF token is available
 *
 * @returns true if token exists, false otherwise
 */
export function hasCsrfToken(): boolean {
  return csrfToken !== null;
}

/**
 * Clear CSRF token (on logout)
 */
export function clearCsrfToken(): void {
  csrfToken = null;
  logger.log('[CSRF] Token cleared');
}
